import { useCallback, useEffect, useRef, useState } from "react";
import { zim } from "../../../../zegoConfig";
import { loginToZego } from "../../../../components/utils/zegoLogin";
import { TeleconsultationData } from "../types";

interface UseZegoChatReturn {
  isLoggedIn: boolean;
  isGroupReady: boolean;
  userId: string;
  userDokter: string;
  groupId: string | null;
  ensureGroupReady: () => Promise<boolean>;
}

const LOGIN_MAX_ATTEMPT = 5;
const JOIN_MAX_ATTEMPT = 3;

/**
 * Manages ZIM authentication and group joining.
 *
 * IMPORTANT: We intentionally do NOT call leaveGroup on unmount.
 * The user is added to the group from the backend when the consultation is created.
 * Leaving the group would cause missed messages when the user navigates away and returns.
 * The user should remain a group member for the full duration of the consultation.
 *
 * Catatan penting soal race saat pasien masuk otomatis dari ConnectingDoctor:
 * group Zego baru dibuat backend tepat ketika dokter menekan "mulai konsultasi",
 * sementara sesi ZIM pasien sudah login lebih dulu di halaman ConnectingDoctor.
 * Karena itu join group bisa gagal di percobaan pertama, jadi hasil join
 * diekspos lewat `ensureGroupReady()` supaya pemanggil bisa menunggu &
 * mencoba ulang sebelum menarik history dari ZIM.
 */
export function useZegoChat(
  teleconsultationData: TeleconsultationData | null,
): UseZegoChatReturn {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isGroupReady, setIsGroupReady] = useState(false);

  // Menyimpan userId yang sedang/sudah diproses login, bukan sekadar boolean,
  // supaya pergantian user tetap memicu login ulang.
  const loginStartedRef = useRef<string | null>(null);
  const loginSucceededRef = useRef(false);
  const joinPromiseRef = useRef<{ key: string; promise: Promise<boolean> } | null>(
    null,
  );

  // ─── Derived identifiers ──────────────────────────────────────────────────

  const appEnv = process.env.REACT_APP_ZEGOCLOUD_APP_ENV || "";

  const userId = teleconsultationData?.patient_id
    ? `LMS${teleconsultationData.patient_id}${appEnv}`
    : "";

  const username = teleconsultationData?.patient_name || "defaultUser";

  const userDokter = teleconsultationData?.practitioner_id
    ? `LMS${teleconsultationData.practitioner_id}${appEnv}`
    : "";

  const groupId = teleconsultationData?.room_id
    ? String(teleconsultationData.room_id).trim()
    : null;

  // ─── Join group ───────────────────────────────────────────────────────────

  const joinGroup = useCallback(
    async (gId: string): Promise<boolean> => {
      if (!gId) {
        console.error("❌ groupId kosong");
        return false;
      }

      console.log("👥 Mencoba joinGroup:", gId);

      try {
        await zim.joinGroup(gId);
        console.log("✅ Berhasil joinGroup:", gId);
        return true;
      } catch (error: any) {
        console.warn("⚠️ joinGroup error detail:", JSON.stringify(error));

        const errorCode =
          error?.code ?? error?.errorCode ?? error?.error?.code ?? 0;
        const errorMessage = error?.message ?? error?.error?.message ?? "";

        // Sudah member → anggap sukses, ini kondisi normal
        if (
          errorCode === 6000522 ||
          errorCode === 108037 ||
          errorMessage?.includes("already belong")
        ) {
          console.log("✅ Sudah member group, lanjut:", gId);
          return true;
        }

        // Group belum ada → buat baru
        if (errorCode === 660500002 || errorCode === 6000404) {
          try {
            console.log("🔄 Group tidak ada, mencoba createGroup:", gId);
            await zim.createGroup({ groupID: gId, groupName: gId }, [
              userDokter,
            ]);
            console.log("✅ Berhasil createGroup:", gId);
            return true;
          } catch (createError: any) {
            const createCode = createError?.code ?? createError?.errorCode ?? 0;
            const createMsg = createError?.message ?? "";
            if (
              createCode === 660500001 ||
              createMsg?.includes("already belong")
            ) {
              console.log("✅ Group sudah ada (race condition), lanjut:", gId);
              return true;
            }
            console.error("❌ Gagal createGroup:", createCode, createMsg);
            return false;
          }
        }

        console.error(
          "❌ Gagal joinGroup (unhandled):",
          errorCode,
          errorMessage,
        );
        return false;
      }
    },
    [userDokter],
  );

  /**
   * Menjamin user sudah tergabung ke group sebelum query history ZIM.
   * Promise-nya di-cache per (user, group) supaya join tidak dipanggil berkali-kali,
   * tetapi percobaan yang gagal tidak ikut di-cache agar bisa dicoba lagi.
   */
  const ensureGroupReady = useCallback(async (): Promise<boolean> => {
    if (!groupId || !isLoggedIn) return false;

    const key = `${userId}:${groupId}`;
    const cached = joinPromiseRef.current;
    if (cached && cached.key === key) return cached.promise;

    const promise = joinGroup(groupId).then((joined) => {
      if (!joined && joinPromiseRef.current?.key === key) {
        joinPromiseRef.current = null; // izinkan retry pada pemanggilan berikutnya
      }
      setIsGroupReady(joined);
      return joined;
    });

    joinPromiseRef.current = { key, promise };
    return promise;
  }, [groupId, isLoggedIn, joinGroup, userId]);

  // ─── Login ZIM ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!userId || !username) return;
    if (loginStartedRef.current === userId) return;
    loginStartedRef.current = userId;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const loginZIM = async () => {
      attempt += 1;
      console.log(
        `🔑 Attempting ZIM Login (percobaan ${attempt}) | userId:`,
        userId,
        "| username:",
        username,
      );

      let success = false;
      try {
        success = await loginToZego(userId, username);
      } catch (error) {
        console.error("❌ ZIM Login Exception:", error);
      }

      if (cancelled) return;

      if (success) {
        console.log("✅ ZIM Login Success | userId:", userId);
        loginSucceededRef.current = true;
        setIsLoggedIn(true);
        return;
      }

      // Login gagal: coba lagi beberapa kali. Tanpa retry, isLoggedIn akan
      // tersangkut false selamanya dan chat baru muncul setelah halaman direfresh.
      if (attempt < LOGIN_MAX_ATTEMPT) {
        const delay = Math.min(1000 * attempt, 5000);
        console.warn(`⚠️ ZIM Login gagal, retry dalam ${delay}ms`);
        retryTimer = setTimeout(loginZIM, delay);
      } else {
        console.error("❌ ZIM Login gagal setelah beberapa percobaan");
        loginStartedRef.current = null; // izinkan percobaan baru bila data berubah
      }
    };

    loginZIM();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      // Bila effect dibersihkan sebelum login sukses (mis. StrictMode atau
      // data telekonsultasi berubah), izinkan percobaan login berikutnya.
      if (!loginSucceededRef.current) {
        loginStartedRef.current = null;
      }
    };
  }, [userId, username]);

  // Reset status join ketika pindah room/konsultasi.
  // Didaftarkan sebelum effect join supaya tidak menghapus promise join
  // yang baru saja dibuat pada mount pertama.
  useEffect(() => {
    joinPromiseRef.current = null;
    setIsGroupReady(false);
  }, [groupId]);

  // ─── Join group setelah login ─────────────────────────────────────────────

  useEffect(() => {
    if (!isLoggedIn || !groupId) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    // Group Zego dibuat backend tepat saat dokter memulai konsultasi, sehingga
    // percobaan join pertama bisa datang lebih dulu daripada group-nya.
    const tryJoin = async () => {
      attempt += 1;
      const joined = await ensureGroupReady();
      if (cancelled || joined) return;
      if (attempt < JOIN_MAX_ATTEMPT) {
        retryTimer = setTimeout(tryJoin, 1500 * attempt);
      }
    };

    tryJoin();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isLoggedIn, groupId, ensureGroupReady]);

  // ─── TIDAK ada leaveGroup saat unmount ────────────────────────────────────
  // User tetap sebagai member group selama sesi konsultasi berlangsung.
  // Jika di-leave, pesan dokter tidak akan masuk saat user di luar halaman chat.
  // Backend yang menghandle penambahan & penghapusan member group.

  return { isLoggedIn, isGroupReady, userId, userDokter, groupId, ensureGroupReady };
}
